"use client";

import { Button } from "@universe/ui/components/button";
import { useIsMobile } from "@universe/ui/hooks/use-mobile";
import { cn } from "@universe/ui/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

import CurrencyFlag from "@/components/currency-flag";
import { ClientErrorBoundary } from "@/components/error/ClientErrorBoundary";
import BlockchainWithdrawalsDetails from "@/components/ledger/details/blockchain-withdrawals-details";
import PaymentDetails from "@/components/ledger/details/payments-details";
import LedgerDirectionIcon from "@/components/ledger/ledger-direction-icon";
import { TransactionStatusBadgeType } from "@/components/ledger/transaction-status-badge";
import { JournalType } from "@/components/ledger/types";
import { NoTranslate } from "@/components/no-translate";
import { PayConfirmationActions } from "@/components/pay/pay-confirmation-actions";
import { ProcessingItem } from "@/components/payments/processing-payments-list";
import { UnconfirmedItem } from "@/components/payments/unconfirmed-payments-list";
import { ResponsiveDialog } from "@/components/responsive-dialog";
import { useSupportEmail } from "@/hooks/useSupportEmail";
import { CancelledError } from "@/lib/errors";
import { logError } from "@/lib/logError";
import { useCustomerAccount, useOtp } from "@/providers";
import type { PaymentResourceType } from "@/types/payments";
import { getBeneficiaryDisplayName } from "@/utils/getLegalEntityName";
import {
  type CreateOtopFunction,
  createOtpResendHandler,
  createOtpVerificationHandler,
  type VerifyOtopFunction,
} from "@/utils/otpHelpers";
import {
  OtopRequestResourceType,
  OtopRequestState,
  useCreateOtopMutation,
  useGetBlockchainWithdrawalRequirementsQuery,
  useGetPaymentRequirementsQuery,
  useVerifyOtopMutation,
} from "@generated/redux/services/generated/api";
import { InstructionState, ScaMethod } from "@generated/types";

interface PaymentDrawerDetailsProps {
  payment: UnconfirmedItem | ProcessingItem | null;
  className?: string;
  onClose: () => void;
  preventClose?: boolean;
  onPaymentConfirmed?: () => void;
  onPaymentCancelled?: (paymentId: string) => void;
  showActionsTab?: boolean;
  statusOverride?: string;
}

type TabType = "actions" | "details";

export default function PaymentDrawerDetails({
  payment,
  className,
  onClose,
  preventClose = false,
  onPaymentConfirmed,
  onPaymentCancelled,
  showActionsTab: showActionsTabProp = true,
  statusOverride,
}: PaymentDrawerDetailsProps) {
  const tCommon = useTranslations("common");
  const tPayments = useTranslations("payments");
  const tLedgerFallback = useTranslations("ledger.fallback");
  const tOtp = useTranslations("otp");
  const supportEmail = useSupportEmail();
  const { showOtpModal, isOtpInProgress } = useOtp();
  const { isAccountSuspended } = useCustomerAccount();
  const isMobile = useIsMobile();

  // View-only (suspended) accounts can inspect payment details but never see
  // the confirm/cancel actions tab. Shadowing the prop keeps every downstream
  // consumer (tab reset, requirements-query skips, tab bar) consistent.
  const showActionsTab = showActionsTabProp && !isAccountSuspended;

  // Store the payment locally to maintain it during closing animation
  const [localPayment, setLocalPayment] = useState<
    UnconfirmedItem | ProcessingItem | null
  >(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>("actions");

  // OTP mutations
  const [createOtop] = useCreateOtopMutation();
  const [verifyOtop] = useVerifyOtopMutation();
  const otpRequestIdRef = useRef<string | null>(null);

  // Track current payment ID to ensure requirements match
  const currentPaymentIdRef = useRef<string | null>(null);

  // Track close timeout to prevent race conditions when rapidly opening/closing
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Update local payment when payment prop changes
  useEffect(() => {
    if (payment) {
      // Clear any pending close timeout since we're opening a new payment
      // This prevents the stale timeout from wiping out the newly selected payment
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }

      // Check if we're switching to a different payment (use ref to avoid stale closure)
      const isSwitchingPayment =
        currentPaymentIdRef.current &&
        currentPaymentIdRef.current !== payment.id;
      const isNewPayment = !currentPaymentIdRef.current;

      // Payment exists - update local state with new data
      setLocalPayment(payment);
      // Update current payment ID ref to track which payment's requirements we're using
      currentPaymentIdRef.current = payment.id ?? null;

      // Only reset tab when opening a new payment or switching payments
      if (isNewPayment || isSwitchingPayment) {
        setActiveTab(showActionsTab ? "actions" : "details");
      }
      // Only clear OTP state when switching to a different payment AND OTP is not in progress
      if (isSwitchingPayment && !isOtpInProgress) {
        otpRequestIdRef.current = null;
      }
    }
    // Note: We don't call onClose() here when payment becomes null.
    // The closing is handled by handleOpenChange which is called from:
    // - onPaymentCancelled callback
    // - auto-close useEffect
    // - user clicking close button
  }, [payment, showActionsTab, isOtpInProgress]);

  // Determine payment type from resourceType
  const resourceType = localPayment?.resourceType as string;
  const isPayment = resourceType === "payments";
  const isBlockchainWithdrawal = resourceType === "blockchain-withdrawals";

  // Check if payment is in CONFIRMED state - skip requirements queries if so
  const isConfirmed =
    localPayment?.instructionState === InstructionState.Confirmed;

  // Fetch confirmation requirements based on resource type
  // Only fetch if showActionsTab is true (i.e., for unconfirmed payments that need confirmation)
  // Skip if payment is already CONFIRMED
  // refetchOnMountOrArgChange: true ensures fresh data is always fetched (no stale cache)
  const paymentRequirements = useGetPaymentRequirementsQuery(
    { uuid: localPayment?.id || "" },
    {
      skip: !isPayment || !showActionsTab || isConfirmed,
      refetchOnMountOrArgChange: true,
    }
  );

  const withdrawalRequirements = useGetBlockchainWithdrawalRequirementsQuery(
    { uuid: localPayment?.id || "" },
    {
      skip: !isBlockchainWithdrawal || !showActionsTab || isConfirmed,
      refetchOnMountOrArgChange: true,
    }
  );

  // Select the active query result based on payment type.
  // The drawer stays mounted while the list switches the selected payment, so
  // the query argument changes under it. RTK Query's `data` is the hook's last
  // result for ANY argument: reading it showed the previous payment's
  // confirmers and "no confirmation required" verdict under the newly selected
  // payment until its own request returned. `currentData` is only ever the
  // selected payment's result, and it stays on screen during a background
  // refetch of that same payment.
  const {
    currentData: confirmationRequirements,
    isFetching: isFetchingRequirements,
    isUninitialized: isUninitializedRequirements,
    error: requirementsError,
    refetch: refetchRequirements,
  } = isPayment ? paymentRequirements : withdrawalRequirements;

  // Nothing for the selected payment has loaded yet: the first open, or a
  // switch to another payment whose request is still in flight. RTK's
  // `isLoading` is false on a switch (the hook already holds a result), so it
  // cannot gate the loading state here.
  const isLoadingRequirements =
    isFetchingRequirements && confirmationRequirements === undefined;

  // RTK Query throws "Cannot refetch a query that has not been started yet"
  // when `.refetch()` is called on a query that was skipped. The requirements
  // query is skipped for confirmed payments (see skip conditions above), so
  // any onRefresh path that fires after a Confirmed-payment action must guard
  // with this flag before refetching.
  const safeRefetchRequirements = async () => {
    if (!isUninitializedRequirements) {
      await refetchRequirements();
    }
  };

  // Extract scaMethod from the local payment
  const scaMethod = localPayment?.scaMethod;

  // Auto-close modal when the payment reaches a terminal-for-user state.
  // Two close paths:
  //   1. Confirmed & requirements satisfied — normal happy path.
  //   2. Cancelled — user- or backend-initiated. The button-click cancel
  //      path already calls handleOpenChange via onPaymentCancelled, but a
  //      BACKEND-initiated cancel (polling delivers the new state to
  //      localPayment) has no other closer and would otherwise leave stale
  //      action buttons in an open drawer.
  useEffect(() => {
    if (!confirmationRequirements || !localPayment) return;

    const { confirmersRequired, currentConfirmers, scaRequired } =
      confirmationRequirements;

    const confirmersAreSatisfied =
      currentConfirmers.length === confirmersRequired;
    const scaIsSatisfied = scaRequired === false;
    const paymentIsConfirmed =
      localPayment.instructionState === InstructionState.Confirmed;
    const paymentIsCancelled =
      localPayment.instructionState === InstructionState.Cancelled;

    if (
      paymentIsCancelled ||
      (paymentIsConfirmed && confirmersAreSatisfied && scaIsSatisfied)
    ) {
      handleOpenChange(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmationRequirements, localPayment]);

  // Clear local payment after closing animation completes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
      // Delay clearing local state to allow closing animation to complete
      closeTimeoutRef.current = setTimeout(() => {
        setLocalPayment(null);
        currentPaymentIdRef.current = null;
        setActiveTab(showActionsTab ? "actions" : "details");
        // Only clear OTP request ID if OTP is not in progress
        if (!isOtpInProgress) {
          otpRequestIdRef.current = null;
        }
        closeTimeoutRef.current = null;
      }, 300);
    }
  };

  // Handle OTP request for unconfirmed payments
  const handleRequestOtp = async () => {
    if (!localPayment) return;

    const paymentAmount = localPayment.amount ?? 0;
    const paymentCurrency =
      localPayment._type === "payment"
        ? localPayment.currency || ""
        : localPayment.beneficiary?.asset?.ledgerProduct || "";
    const recipientName =
      getBeneficiaryDisplayName(localPayment.beneficiary) ||
      tCommon("notAvailable");

    // Format the amount with currency
    const formattedAmount = `${paymentAmount.toFixed(2)} ${paymentCurrency}`;

    try {
      // Create OTOP request BEFORE showing modal
      const messageKey = recipientName
        ? "messageFormat.verifyPayment"
        : "messageFormat.verifyPaymentGeneric";

      const messageFormat = recipientName
        ? tOtp(messageKey, { recipientName, formattedAmount, supportEmail })
        : tOtp(messageKey, { formattedAmount, supportEmail });

      const result = await createOtop({
        resourceType: (isPayment
          ? "payments"
          : "blockchain-withdrawals") as OtopRequestResourceType,
        resourceTypeId: localPayment.id ?? "",
        createOtopRequest: {
          messageFormat,
        },
      }).unwrap();

      otpRequestIdRef.current = result.id;

      // Flow-specific action after OTP verification completes
      const onCompletedAction = async () => {
        // Wait for success toast to be visible (1 seconds)
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Refetch requirements to get updated status (no-op if query was skipped)
        await safeRefetchRequirements();
        // Refetch unconfirmed payments to update the list
        // This will update the payment prop, which will trigger useEffect to update localPayment
        onPaymentConfirmed?.();
        // Keep modal open - let the payment prop update handle closing if needed
      };

      // Show modal with code entry screen
      await new Promise<void>((resolve, reject) => {
        showOtpModal({
          showTrustScreen: false,
          recipientName,
          flowType: "payment",
          paymentAmount,
          paymentCurrency,
          expiryTime: result.expiryTime,

          onSubmitCode: createOtpVerificationHandler({
            verifyOtop: verifyOtop as unknown as VerifyOtopFunction,
            otpRequestIdRef,
            tOtp,
            onCompletedAction,
            supportEmail,
          }) as unknown as (
            code: string
          ) => Promise<{ state: OtopRequestState }>,

          onResendCode: createOtpResendHandler({
            beneficiary: localPayment.beneficiary,
            resourceType: (isPayment
              ? "payments"
              : "blockchain-withdrawals") as OtopRequestResourceType,
            resourceTypeId: localPayment.id ?? "",
            createOtop: createOtop as unknown as CreateOtopFunction,
            messageKeyBase: "verifyPayment",
            tOtp,
            otpRequestIdRef,
            additionalMessageParams: { formattedAmount, supportEmail },
          }),
          onSuccess: () => {
            resolve();
          },
          onCancel: () => {
            otpRequestIdRef.current = null;
            reject(new CancelledError("Payment verification cancelled"));
          },
        });
      });
    } catch (error) {
      logError("Error in payment OTP flow", error);
    }
  };

  const renderPaymentDetails = () => {
    if (!localPayment) return null;

    // Create mock ledger entry objects to reuse existing detail components
    if (localPayment._type === "payment") {
      const amount = localPayment.amount ?? 0;
      const mockLedgerEntry = {
        journal: "payments",
        uuid: localPayment.id,
        item: 0,
        ledgerCode: 0,
        product: localPayment.currency,
        productType: "currencies",
        credit: 0,
        debit: amount,
        bookDate: localPayment.paymentDate,
        valueDate: localPayment.valueDate || localPayment.paymentDate,
        balance: -amount,
        reason: "pending-payment",
        resolvedData: localPayment,
      } as LedgerEntryResolvedPayments;

      return (
        <div className="space-y-0 mx-3 lg:mx-0">
          <PaymentDetails
            entry={mockLedgerEntry}
            showInstructionStatus={true}
            amountLabel={tCommon("amount")}
            statusOverride={statusOverride as TransactionStatusBadgeType}
          />
        </div>
      );
    }

    if (localPayment._type === "withdrawal") {
      const amount = localPayment.amount ?? 0;
      const mockLedgerEntry = {
        journal: "blockchain-withdrawals",
        uuid: localPayment.id,
        item: 0,
        ledgerCode: 0,
        product: localPayment.beneficiary?.asset?.ledgerProduct || "",
        productType: "digital-assets",
        credit: 0,
        debit: amount,
        bookDate: localPayment.withdrawalDate || "",
        valueDate: localPayment.withdrawalDate || "",
        balance: -amount,
        reason: "pending-withdrawal",
        resolvedData: localPayment,
      } as LedgerEntryResolvedBlockchainWithdrawal;

      return (
        <div className="space-y-0 mx-3 lg:mx-0">
          <BlockchainWithdrawalsDetails
            entry={mockLedgerEntry}
            showInstructionStatus={true}
            amountLabel={tCommon("amount")}
            statusOverride={statusOverride as TransactionStatusBadgeType}
          />
        </div>
      );
    }

    return null;
  };

  const title = localPayment ? (
    <div className="flex items-center gap-1.5 overflow-hidden">
      <LedgerDirectionIcon
        journalType={
          isPayment ? JournalType.PAYMENTS : JournalType.BLOCKCHAIN_WITHDRAWALS
        }
        balance={-(localPayment.amount ?? 0)}
        isFee={false}
        className="lg:h-7 lg:w-7"
      />
      <div className="mr-1.5">
        <CurrencyFlag
          size={!isMobile ? "26px" : "24px"}
          currency={
            localPayment._type === "payment"
              ? localPayment.currency || ""
              : localPayment.beneficiary?.asset?.ledgerProduct || ""
          }
        />
      </div>
      <div className="flex items-center">
        <p
          className="font-medium text-base md:text-lg truncate max-w-[calc(100vw-150px)] sm:w-[330px] lg:w-[350px]"
          data-testid="payment-drawer-title"
        >
          <NoTranslate>
            {getBeneficiaryDisplayName(localPayment.beneficiary) ||
              tLedgerFallback("sent", {
                product:
                  localPayment._type === "payment"
                    ? localPayment.currency || ""
                    : localPayment.beneficiary?.asset?.ledgerProduct || "",
              })}
          </NoTranslate>
        </p>
      </div>
    </div>
  ) : null;

  // Render Actions tab content
  const renderActionsTab = () => {
    if (!localPayment) return null;

    const isUnconfirmed =
      localPayment.instructionState === InstructionState.Unconfirmed;

    // Show loading state while determining what to show
    if (!isConfirmed && isLoadingRequirements) {
      return (
        <div className="mx-3 lg:mx-0 space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
            <span>{tPayments("gettingPaymentStatus")}</span>
          </div>
        </div>
      );
    }

    // Handle error state (404 is acceptable - still show component)
    if (requirementsError) {
      const status = (requirementsError as { status?: number })?.status;
      if (status === 404) {
        // 404 is acceptable - show message that no actions are required
        return (
          <div className="p-4 text-center text-sm text-muted-foreground">
            {tPayments("noConfirmationRequirements")}
          </div>
        );
      }
      // Log other errors but continue showing page
      logError("Error fetching confirmation requirements", requirementsError);
      return (
        <div className="p-4 text-center text-sm text-red-600">
          {tCommon("errorLoadingRequirements")}
        </div>
      );
    }

    // Always render PayConfirmationActions - it handles its own loading/processing state
    return (
      <div className="mx-3 lg:mx-0 space-y-4">
        {isUnconfirmed && (
          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg flex gap-3 items-center">
            <AlertCircle className="h-5 w-5 text-orange-800 flex-shrink-0 mt-0.5" />
            <p className="text-xs font-semibold text-orange-800">
              {tCommon("unconfirmedPaymentMessage")}
            </p>
          </div>
        )}
        <PayConfirmationActions
          confirmationRequirements={confirmationRequirements}
          isFetchingRequirements={isFetchingRequirements}
          requirementsError={requirementsError}
          onRequestOtp={handleRequestOtp}
          isOtpInProgress={isOtpInProgress}
          scaMethod={scaMethod ? (scaMethod as ScaMethod) : undefined}
          paymentId={localPayment?.id}
          isLoading={isLoadingRequirements}
          instructionState={localPayment?.instructionState}
          processingState={localPayment?.processingState}
          resourceType={resourceType as PaymentResourceType}
          onRefresh={async () => {
            // Refetch requirements after action completion (no-op when the
            // query was skipped, e.g. for already-Confirmed payments).
            await safeRefetchRequirements();
            // Refetch unconfirmed payments list
            onPaymentConfirmed?.();
          }}
          rereadRequirements={async () => {
            // A skipped query has nothing to read (see safeRefetchRequirements).
            // `refetch` always goes to the network, past the cached result.
            if (isUninitializedRequirements) return undefined;
            return refetchRequirements().unwrap();
          }}
          onPaymentCancelled={() => {
            onPaymentCancelled?.(localPayment?.id ?? "");
            handleOpenChange(false);
          }}
        />
      </div>
    );
  };

  const footer = (
    <div className="flex flex-col gap-3 w-full">
      <Button
        onClick={() => handleOpenChange(false)}
        className="w-full h-11 text-base"
        data-testid="close-payment-drawer-button"
      >
        {tCommon("close")}
      </Button>
    </div>
  );

  return (
    <ResponsiveDialog
      open={!!payment}
      onOpenChange={handleOpenChange}
      title={title}
      className={className}
      preventClose={preventClose}
      footer={footer}
      showDefaultFooter={false}
    >
      {localPayment && (
        <div>
          {/* Tabs */}
          {showActionsTab && (
            <div className="mb-4">
              <div className="flex items-center gap-6 border-b border-line mx-3 lg:mx-0">
                {/* Actions Tab */}
                <button
                  onClick={() => setActiveTab("actions")}
                  className={cn(
                    "relative pb-3 px-1 font-medium text-base transition-colors hover:cursor-pointer",
                    activeTab === "actions"
                      ? "text-primary"
                      : "text-muted-foreground hover:text-gray-700"
                  )}
                >
                  <span>{tPayments("actions")}</span>
                  <AnimatePresence>
                    {activeTab === "actions" && (
                      <motion.div
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      />
                    )}
                  </AnimatePresence>
                </button>

                {/* Details Tab */}
                <button
                  onClick={() => setActiveTab("details")}
                  className={cn(
                    "relative pb-3 px-1 font-medium text-base transition-colors hover:cursor-pointer",
                    activeTab === "details"
                      ? "text-primary"
                      : "text-muted-foreground hover:text-gray-700"
                  )}
                >
                  <span>{tPayments("details")}</span>
                  <AnimatePresence>
                    {activeTab === "details" && (
                      <motion.div
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      />
                    )}
                  </AnimatePresence>
                </button>
              </div>
            </div>
          )}

          {/* Tab Content */}
          {activeTab === "actions" ? (
            <div>{renderActionsTab()}</div>
          ) : (
            <div>
              <ClientErrorBoundary>
                {renderPaymentDetails()}
              </ClientErrorBoundary>
            </div>
          )}
        </div>
      )}
    </ResponsiveDialog>
  );
}
